import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { IMessageContext } from '../../interfaces/message.interface';

interface GroupedSource {
  context: IMessageContext;
  refNumbers: number[];
  title: string;
  icon: string;
  anonymous: boolean;
}

@Component({
  selector: 'app-source-pills',
  templateUrl: './source-pills.component.html',
  styleUrls: ['./source-pills.component.scss']
})
export class SourcePillsComponent implements OnInit, OnChanges {
  @Input() contexts: IMessageContext[] = [];

  groupedSources: GroupedSource[] = [];
  isExpanded = false;
  showExpandButton = false;

  ngOnInit(): void {
    this.groupSourcesByDocument();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contexts'] && changes['contexts'].currentValue) {
      this.groupSourcesByDocument();
    }
  }

  /**
   * Group sources by document (deduplicate based on source URL/ID)
   * Each document appears only once, but with multiple reference numbers if cited multiple times
   */
  private groupSourcesByDocument(): void {
    const sourceMap = new Map<string, GroupedSource>();

    for (const context of this.contexts) {
      // Create a unique key for this document
      const key = this.getGroupKey(context);

      if (sourceMap.has(key)) {
        // Document already exists, just add the reference number
        const existing = sourceMap.get(key)!;
        if (context.refNumber && !existing.refNumbers.includes(context.refNumber)) {
          existing.refNumbers.push(context.refNumber);
        }
      } else {
        // New document, create entry
        sourceMap.set(key, {
          context,
          refNumbers: context.refNumber ? [context.refNumber] : [],
          title: this.getTitle(context),
          icon: this.getIconForDocType(context.docType),
          anonymous: context.anonymous === true,
        });
      }
    }

    // Convert map to array and sort by first reference number
    this.groupedSources = Array.from(sourceMap.values()).sort((a, b) => {
      const aFirst = a.refNumbers[0] || 0;
      const bFirst = b.refNumbers[0] || 0;
      return aFirst - bFirst;
    });

    // Show expand button if there are more than 2 sources (to save space)
    this.showExpandButton = this.groupedSources.length > 2;
  }

  /**
   * Grouping key: one key per *document*, not per ingest entry.
   *
   * A `website` ingest entry crawls many pages. Every crawled page is its own document
   * with its own `metadata.source` page URL, but they all share a single
   * `ingest_entry_id`. Keying on the entry id alone therefore collapsed every cited page
   * of a site into one pill; that pill kept the *first* context and `openSource()` opened
   * its `plainUrl` — so the pill was named correctly while the link pointed at a
   * different page of the same site (CB-407).
   *
   * The per-document discriminator is the raw source URL as it arrives on the context:
   * the context-level `source` first (the v2 inquiry path copies `metadata.source` onto
   * it, so the two coincide there), then `metadata.source`, `metadata.urlOverride` and
   * `metadata.url`. Those three metadata fields are exactly the order the server's
   * `getPlainUrl()` reads them in (`server/src/utils/dataset.utils.ts`: `metadata.source
   * || metadata.urlOverride || metadata.url`), so this key mirrors that precedence. The
   * *parsing* does not mirror it. `getPlainUrl()` additionally
   * `.trim()`s and unwraps a markdown-link-shaped source (`[Title](url)`) — this key does
   * neither, so the two are not interchangeable values, only the same precedence. That is
   * fine for grouping: the raw string is stable per document, so two chunks of one
   * document still yield one key.
   *
   * Deliberately NOT `plainUrl`: `plainUrl` can be a freshly signed URL whose
   * `expires`/`signature` differ between two chunks of the *same* document (see
   * `server/src/services/url-signing.service.ts`), which would split e.g. a multi-page PDF
   * into one pill per chunk, and differently on every render. Chunks of one document share
   * one raw source URL, so those still collapse into a single pill.
   *
   * Legacy contexts (built in `server/src/utils/dataset.utils.ts`) carry full `metadata`
   * but no context-level `source` and no `ingest_entry_id`, so they key on
   * `metadata.source` — which `buildBaseMetadata` (`management-app/api/src/loaders/utils/
   * metadata.utils.ts`) always stamps, while `metadata.url` may be absent — rather than on
   * the per-call-signed `plainUrl` the old chain used. `plainUrl` remains the fallback
   * only for contexts with no URL in metadata at all; anonymous, label-only contexts (no
   * entry id, no URL, empty `plainUrl`) fall through to the label.
   *
   * Reading `metadata.source` ahead of `metadata.urlOverride` only changes anything where
   * the two differ. `buildBaseMetadata` sets `source = urlOverride` whenever an
   * `urlOverride` exists (`metadata.utils.ts:94-104`), so for every loader going through
   * it the two are identical. The one producer that makes them differ is
   * `management-app/api/src/loaders/odata.loader.ts:158`, which appends `#<recordId>` per
   * record — there the OData records of one entry now get one pill each instead of
   * collapsing onto the shared `urlOverride`, which is the fix, not a regression: each
   * record's `plainUrl` is its own `source`, so the collapsed pill used to open the first
   * record while carrying another record's label.
   *
   * Known hole — this fix silently does not apply when a `website` ingest entry's config
   * carries `baseUrl` or `urlOverride`: `management-app/api/src/loaders/utils/
   * metadata.utils.ts` then stamps *every* crawled page with one identical `source`, so
   * the pages collapse into a single pill again. The ingest UI exposes neither field for
   * `type === 'website'` (only startUrls, matchers, selectors, titleSelectors, maxDepth,
   * maxPages, ignoreSitemap, strategy), but the API accepts them.
   */
  private getGroupKey(context: IMessageContext): string {
    const pageUrl = context.source
      || context.metadata?.source
      || context.metadata?.urlOverride
      || context.metadata?.url;

    if (context.ingest_entry_id) {
      // An entry whose documents carry no source URL at all still collapses into one
      // pill, exactly as before.
      return `${context.ingest_entry_id}::${pageUrl || ''}`;
    }

    return pageUrl || context.plainUrl || context.sourceString;
  }

  /**
   * Get icon name based on document type
   */
  getIconForDocType(docType?: string): string {
    if (!docType) return 'description';

    switch (docType.toLowerCase()) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'pst-email':
      case 'email':
        return 'email';
      case 'image':
      case 'png':
      case 'jpg':
      case 'jpeg':
        return 'image';
      case 'doc':
      case 'docx':
        return 'description';
      case 'xls':
      case 'xlsx':
        return 'table_chart';
      default:
        return 'description';
    }
  }

  /**
   * Get display title from context
   */
  getTitle(context: IMessageContext): string {
    // An anonymous context ships only its label — and no metadata to fall back to.
    if (context.anonymous) {
      return context.sourceString;
    }

    // Try to get title from metadata
    if (context.metadata?.title) {
      return context.metadata.title;
    }

    // For emails, use subject
    if (context.metadata?.subject) {
      return context.metadata.subject;
    }

    // Fallback to sourceString or filename
    if (context.sourceString) {
      return context.sourceString;
    }

    // Extract filename from URL
    if (context.plainUrl) {
      const parts = context.plainUrl.split('/');
      return parts[parts.length - 1] || 'Dokument';
    }

    return 'Dokument';
  }

  /**
   * Format reference numbers for display (e.g., "[1, 2, 5]" or "[1]")
   */
  formatRefNumbers(refNumbers: number[]): string {
    if (refNumbers.length === 0) return '';
    if (refNumbers.length === 1) return `[${refNumbers[0]}]`;

    // Sort and format
    const sorted = [...refNumbers].sort((a, b) => a - b);
    return `[${sorted.join(', ')}]`;
  }

  /**
   * Open source in new tab or trigger download.
   * When `skipPreviewDialog` is set on the source metadata (resolved server-side from the
   * entry / connector / dataset hierarchy at ingestion time), navigate directly to the
   * `urlOverride` instead of opening the preview dialog.
   */
  openSource(source: GroupedSource): void {
    // Nothing to open: an anonymous source has no URL, by design.
    if (source.anonymous) {
      return;
    }

    // Check if skipPreviewDialog is enabled via metadata (set during ingestion from connector/entry config)
    const shouldSkipDialog = source.context.metadata?.skipPreviewDialog === true;

    if (shouldSkipDialog && source.context.metadata?.urlOverride) {
      // Navigate directly to the external document URL (e.g. external DMS web UI)
      window.open(source.context.metadata.urlOverride, '_blank');
    } else {
      // Default behavior: open s3Url or plainUrl
      const url = source.context.plainUrl;
      if (url) {
        if (url.startsWith('v1/files')) {
          // If it's a file served by our backend, make the absolute url from /api/v1/files/...
          const absoluteUrl = `${window.location.origin}/api/${url}`;
          window.open(absoluteUrl, '_blank');
        } else {
          window.open(url, '_blank');
        }
      } else if (source.context.s3Url) {
        // s3Url is an internal MinIO URL; never open directly.
        // Skip — the file is not accessible without a signed backend URL.
      }
    }
  }

  /**
   * Scroll to this pill (for highlighting when clicking inline references)
   */
  scrollToPill(refNumber: number): void {
    // Find the grouped source that contains this reference number
    const source = this.groupedSources.find(s => s.refNumbers.includes(refNumber));
    if (source) {
      const firstRef = source.refNumbers[0];
      const element = document.getElementById(`source-pill-${firstRef}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        element.classList.add('highlighted');
        setTimeout(() => {
          element.classList.remove('highlighted');
        }, 2000);
      }
    }
  }

  /**
   * Toggle expanded/collapsed state
   */
  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Get visible sources based on expanded state
   */
  get visibleSources(): GroupedSource[] {
    if (this.isExpanded || !this.showExpandButton) {
      return this.groupedSources;
    }
    // Show only 2 sources when collapsed (to keep it compact)
    return this.groupedSources.slice(0, 2);
  }
}
